// Search query layer (search-platform §3.4): GET /api/v1/search/suggest
// (members) and GET /api/v1/search/public-suggest (logged-out visitors) back
// the top-bar search box; GET /api/v1/search/results and
// GET /api/v1/search/public-results back the /search page (cursor
// pagination); POST /api/v1/search/clicks records relevance analytics.

import { useEffect } from 'react';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DomainError } from '@leanin/shared/errors';

import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import { shouldRetry } from '../query-retry';
import { trimLower } from './normalize';
import type {
  SearchResultItem,
  SearchResultsPage,
  SearchResultsScope,
  SearchSuggestPage,
} from '../../components/global-search/types';

// Suggest/results refuse queries under 3 chars server-side; below this the
// UI renders a type-more hint locally without firing a request.
export const MIN_SEARCH_QUERY_LENGTH = 3;

// Which suggest surface to hit: the authed cross-entity one, or the logged-out
// public one scoped to circles/networks/resources (GET /search/public-suggest).
export type SuggestScope = 'all' | 'public';

type SearchResultsArgs = {
  readonly q: string;
  readonly scope: SearchResultsScope;
  readonly pageSize?: number;
};

// ---- Query keys ----

export const searchKeys = {
  all: ['search'] as const,

  // suggest — `q` is trimmed (not lowercased: suggest is case-preserving
  // server-side) in the factory and is the terminal key element. `scope`
  // selects the authed cross-entity surface or the public directory one.
  suggest: (rawQ: string, scope: SuggestScope = 'all') => {
    const q = rawQ.trim();
    return queryOptions({
      queryKey: [...searchKeys.all, 'suggest', scope, q] as const,
      queryFn: () =>
        scope === 'public' ? fetchSearchSuggestPublic(q) : fetchSearchSuggest(q),
      enabled: q.length >= MIN_SEARCH_QUERY_LENGTH,
      staleTime: 30_000,
      placeholderData: keepPreviousData,
    });
  },

  // results — normalized args (`{ q, scope, pageSize? }`) is the terminal key
  // element; the cursor is injected via pageParam, never in the key. `scope`
  // keys the member and public surfaces apart: a sign-in mid-session must
  // not serve the public page's cached results as the member's.
  results: (raw: { q: string; scope: SearchResultsScope; pageSize?: number }) => {
    const args: SearchResultsArgs = {
      q: raw.q.trim(),
      scope: raw.scope,
      ...(raw.pageSize !== undefined ? { pageSize: raw.pageSize } : {}),
    };
    return infiniteQueryOptions({
      queryKey: [...searchKeys.all, 'results', args] as const,
      queryFn: ({ pageParam }) =>
        fetchSearchResults({
          q: args.q,
          scope: args.scope,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
          ...(args.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      enabled: args.q.length >= MIN_SEARCH_QUERY_LENGTH,
      placeholderData: keepPreviousData,
      retry: (failureCount, error) =>
        !isSearchModeChangedError(error) && shouldRetry(failureCount, error),
    });
  },
};

// ---- Suggest ----

export async function fetchSearchSuggest(q: string): Promise<SearchSuggestPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/search/suggest', {
    params: { query: { q } },
  }));
  // Cast through unknown: the generated space payload aliases the wider
  // SpaceSearchHit intersection; the runtime shape matches the narrow local
  // type (see components/global-search/types.ts).
  return data as unknown as SearchSuggestPage;
}

export async function fetchSearchSuggestPublic(
  q: string,
): Promise<SearchSuggestPage> {
  const client = getApiClient();
  const data = unwrap(await client.GET('/api/v1/search/public-suggest', {
    params: { query: { q } },
  }));
  // Public-suggest returns only the groups + resources tabs — a subset of the
  // authed shape, so the narrow local SearchSuggestPage type still holds.
  return data as unknown as SearchSuggestPage;
}

export function useSearchSuggest(query: string, scope: SuggestScope = 'all') {
  return useQuery(searchKeys.suggest(query, scope));
}

// ---- Results ----

export function isSearchModeChangedError(err: unknown): boolean {
  return err instanceof DomainError && err.error.code === 'SEARCH_MODE_CHANGED';
}

export async function fetchSearchResults(args: {
  q: string;
  scope: SearchResultsScope;
  cursor?: string;
  pageSize?: number;
}): Promise<SearchResultsPage> {
  const client = getApiClient();
  const paging = {
    ...(args.cursor !== undefined ? { cursor: args.cursor } : {}),
    ...(args.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
  };
  const data =
    args.scope.kind === 'member'
      ? unwrap(await client.GET('/api/v1/search/results', {
          params: { query: { q: args.q, type: args.scope.type, ...paging } },
        }))
      : unwrap(await client.GET('/api/v1/search/public-results', {
          params: { query: { q: args.q, type: args.scope.type, ...paging } },
        }));
  return data as unknown as SearchResultsPage;
}

export function useInfiniteSearchResults(
  q: string,
  scope: SearchResultsScope,
  pageSize?: number,
) {
  const queryClient = useQueryClient();
  // Capture-once (decision 9): the factory normalizes (trims q); bind the
  // options object once so the read and the 409-reset target the same key.
  const opts = searchKeys.results({ q, scope, ...(pageSize !== undefined ? { pageSize } : {}) });
  const query = useInfiniteQuery(opts);

  // 409 SEARCH_MODE_CHANGED: the cursor was minted under a different
  // retrieval mode (embedder degraded/recovered between pages). Reset
  // pagination silently — resetQueries drops every loaded page and refetches
  // page 1 of the active query (search-platform D9).
  const modeChanged = isSearchModeChangedError(query.error);
  useEffect(() => {
    if (!modeChanged) return;
    void queryClient.resetQueries({ queryKey: opts.queryKey, exact: true });
  }, [modeChanged, queryClient, opts.queryKey]);

  return query;
}

// ---- Clicks ----

export type SearchClickArgs = {
  readonly queryEventId: string;
  readonly kind: SearchResultItem['kind'];
  readonly id: string;
  readonly rank: number;
};

export async function recordSearchClick(args: SearchClickArgs): Promise<void> {
  const client = getApiClient();
  unwrapVoid(await client.POST('/api/v1/search/clicks', {
    body: {
      queryEventId: args.queryEventId,
      kind: args.kind,
      id: args.id,
      rank: args.rank,
    },
  }));
}

// Fire-and-forget from the UI: call `mutate(...)` without awaiting — no UI
// state depends on the outcome, and react-query swallows mutate() rejections.
export function useRecordSearchClick() {
  return useMutation({ mutationFn: recordSearchClick });
}
